import { BaseTariffPage } from "@/components/tarif/BaseTariffPage";
import { Shield, Phone, CheckCircle2, Stethoscope, Users, Building2, Hospital, AlertCircle, Clock, FileText } from "lucide-react";

const HISCOX_COLOR = "#E60012";

const HiscoxTarif = () => {
  const caseStudies = [
    {
      title: "Ransomware-Angriff auf Zahnarztpraxis",
      description: "WannaCry-Ransomware verschlüsselte gesamte Patientendatenbank inkl. digitaler Röntgenbilder. Praxis musste 6 Tage schließen, 200+ Termine absagen.",
      outcome: "Hiscox 24/7-Krisenhotline sofort aktiv. IT-Forensiker binnen 4h vor Ort. Daten aus verschlüsseltem Backup wiederhergestellt. Risk Academy schulte Team danach. Versicherung übernahm 48.700 € (IT-Forensik, Backup-Restore, Umsatzausfall, Patient-Kommunikation).",
      damage: "48.700 €"
    },
    {
      title: "DSGVO-Bußgeld nach Mitarbeiter-Phishing",
      description: "Mitarbeiterin öffnete Phishing-Mail, Hacker luden 380 Patientendaten ab. Datenschutzbehörde verhängte Bußgeld wegen unzureichender Schulung.",
      outcome: "Hiscox koordinierte Rechtsberatung, Behördenmeldung, Patienten-Notification. Durch professionelles Krisenmanagement Bußgeld von 15.000 € auf 5.500 € reduziert. Versicherung übernahm Bußgeld + 7.800 € Anwaltskosten. Praxis erhielt kostenlose Risk Academy Schulungen.",
      damage: "13.300 €"
    },
    {
      title: "CEO-Fraud mit gefälschter Überweisung",
      description: "Gefälschte E-Mail vom 'Praxisinhaber' wies Mitarbeiterin an, 32.000 € auf ausländisches Konto zu überweisen. Betrug nach 3 Tagen entdeckt.",
      outcome: "Hiscox aktivierte Sofortmaßnahmen: Polizei, Bank-Rückhol-Verfahren, forensische Analyse. 8.000 € konnten zurückgeholt werden. Versicherung erstattete restliche 24.000 € plus 5.200 € Forensik & Rechtsbeistand.",
      damage: "29.200 €"
    }
  ];

  const coverageAreas = [
    {
      icon: Shield,
      title: "Eigenschäden",
      items: [
        "Datenwiederherstellung nach Cyberangriff",
        "IT-Forensik und Schadensanalyse",
        "Betriebsunterbrechung & Umsatzausfall",
        "Wiederherstellung von Hard- & Software",
        "Kosten für Krisenmanagement"
      ]
    },
    {
      icon: AlertCircle,
      title: "Cyber-Haftpflicht",
      items: [
        "DSGVO-Bußgelder und Schadensersatz",
        "Datenschutzverletzungen & Meldepflichten",
        "Ansprüche Dritter bei Datenlecks",
        "Abwehr unberechtigter Ansprüche",
        "Rechtsschutz bei Cyber-Vorfällen"
      ]
    },
    {
      icon: Phone,
      title: "24/7 Assistance",
      items: [
        "Cyber-Krisenhotline rund um die Uhr",
        "Sofortige Expertenunterstützung",
        "Koordination aller Dienstleister",
        "PR- und Krisenkommunikation",
        "Psychologische Erstbetreuung"
      ]
    },
    {
      icon: CheckCircle2,
      title: "Prävention & Training",
      items: [
        "Hiscox Risk Academy (kostenlos)",
        "Interaktive Cyber-Schulungen",
        "Mitarbeiter-Sensibilisierung",
        "Zertifizierte Trainings auf Deutsch/Englisch",
        "Regelmäßige Updates zu neuen Bedrohungen"
      ]
    }
  ];

  const targetGroups = [
    {
      icon: Stethoscope,
      title: "Hausarztpraxen",
      description: "Kleinere Praxen mit hoher Patientenfrequenz und sensiblen Gesundheitsdaten",
      benefits: [
        "Absicherung bei Ransomware-Angriffen auf Praxissoftware",
        "Schutz vor Phishing-Attacken auf Mitarbeiter",
        "24/7-Hotline für schnelle Hilfe im Notfall",
        "Kostenlose Cyber-Trainings für das Praxisteam"
      ],
      recommendation: "Empfohlene Deckung: 500.000 - 1.000.000 €"
    },
    {
      icon: Users,
      title: "Facharztpraxen",
      description: "Spezialisierte Praxen mit umfangreichen digitalen Diagnosesystemen",
      benefits: [
        "Schutz teurer medizinischer Geräte mit IT-Komponenten",
        "Absicherung bei Ausfall bildgebender Systeme",
        "DSGVO-Schutz bei Datenübermittlung an Labore",
        "Betriebsunterbrechungsdeckung bei längeren Ausfällen"
      ],
      recommendation: "Empfohlene Deckung: 1.000.000 - 2.000.000 €"
    },
    {
      icon: Building2,
      title: "MVZ & Praxen",
      description: "Mehrere Ärzte und Standorte mit vernetzten IT-Systemen",
      benefits: [
        "Schutz aller Standorte unter einem Vertrag",
        "Absicherung komplexer IT-Infrastrukturen",
        "Höhere Deckungssummen für größere Schäden",
        "Cyber Risk Academy für alle Mitarbeiter"
      ],
      recommendation: "Empfohlene Deckung: 2.000.000 - 5.000.000 €"
    },
    {
      icon: Hospital,
      title: "Kliniken & Krankenhäuser",
      description: "Große medizinische Einrichtungen mit kritischer IT-Infrastruktur",
      benefits: [
        "Maximalschutz für kritische Systeme",
        "Sofortige Krisenintervention bei Großschäden",
        "Deckung von Massenpatientendaten",
        "Spezialisten für Healthcare-Cyberrisiken"
      ],
      recommendation: "Empfohlene Deckung: 5.000.000 - 10.000.000 €"
    }
  ];

  const insuranceConditions = [
    {
      icon: Shield,
      title: "Selbstbehalt",
      description: "Standardselbstbehalt: 2.500 € pro Schadensfall. Reduzierbar auf 1.000 € gegen Mehrbeitrag. Entfällt bei reinen Assistance-Leistungen."
    },
    {
      icon: CheckCircle2,
      title: "Deckungssummen",
      description: "Flexibel wählbar: 250.000 € bis 10.000.000 €. Empfohlen für Einzelpraxen: 1.000.000 €. Empfohlen für MVZ/Kliniken: 3.000.000 € - 5.000.000 €."
    },
    {
      icon: Phone,
      title: "Service & Support",
      description: "24/7 Cyber-Krisenhotline rund um die Uhr erreichbar. Kostenloser Zugang zur Hiscox Risk Academy. Persönlicher Ansprechpartner."
    },
    {
      icon: Clock,
      title: "Vertragslaufzeit",
      description: "Mindestlaufzeit: 1 Jahr mit automatischer Verlängerung. Kündigungsfrist: 3 Monate zum Vertragsende. Sonderkündigungsrecht nach Schadensfall."
    },
    {
      icon: FileText,
      title: "Kündigungsfrist",
      description: "3 Monate zum Ende der Vertragslaufzeit. Bei Nichtkündigung automatische Verlängerung um ein weiteres Jahr. Sonderkündigungsrecht nach jedem regulierten Schadensfall."
    },
    {
      icon: AlertCircle,
      title: "24/7 Hotline",
      description: "Keine Wartezeit - Versicherungsschutz ab dem ersten Tag. Cyber-Krisenhotline rund um die Uhr erreichbar, auch an Wochenenden und Feiertagen."
    }
  ];

  const faqs = [
    {
      question: "Warum ist Hiscox besonders für medizinische Praxen geeignet?",
      answer: "Hiscox ist seit 2011 der erste Cyber-Versicherer auf dem deutschen Markt und bietet spezielle Leistungen wie 24/7-Krisenhotline, kostenlose Cyber-Trainings für Mitarbeiter und eine außergewöhnlich schnelle Schadensregulierung. Die Assistance-Leistungen sind im Versicherungsschutz bereits enthalten."
    },
    {
      question: "Was umfasst die 24/7 Cyber-Krisenhotline?",
      answer: "Im Schadensfall erreichen Sie rund um die Uhr Cyber-Security-Experten, die sofort Maßnahmen einleiten. Die Hotline koordiniert IT-Forensiker, Datenrettung, Rechtsberatung und Krisenkommunikation - alles aus einer Hand."
    },
    {
      question: "Wie funktioniert das kostenlose Cyber-Training?",
      answer: "Alle versicherten Praxen erhalten Zugang zur Hiscox Risk Academy mit interaktiven Online-Trainings zur Sensibilisierung gegenüber Cyber-Gefahren. Die Schulungen sind auf Deutsch und Englisch verfügbar und beinhalten Tests mit Zertifikat."
    },
    {
      question: "Welche Deckungssummen bietet Hiscox?",
      answer: "Hiscox bietet flexible Deckungssummen bis zu 10 Millionen Euro. Für kleine Praxen sind bereits Summen ab 250.000 Euro verfügbar, die individuell an den Bedarf angepasst werden können."
    },
    {
      question: "Sind Ransomware-Angriffe versichert?",
      answer: "Ja, Hiscox deckt Ransomware-Angriffe vollumfänglich ab - inkl. Lösegeldzahlungen (nach Prüfung), IT-Forensik, Datenwiederherstellung, Betriebsunterbrechung und alle damit verbundenen Kosten."
    },
    {
      question: "Wie schnell reagiert Hiscox im Schadensfall?",
      answer: "Die 24/7-Krisenhotline ist sofort erreichbar. Erste Maßnahmen werden innerhalb von Stunden eingeleitet. Die Schadensregulierung erfolgt nach Prüfung typischerweise innerhalb von 2-3 Wochen."
    },
    {
      question: "Gibt es eine Wartezeit nach Vertragsabschluss?",
      answer: "Nein, der Versicherungsschutz beginnt sofort nach Vertragsbeginn ohne Wartezeit. Bereits ab dem ersten Tag sind Sie vollständig geschützt."
    }
  ];

  const documents = [
    {
      title: "Hiscox CyberClear Produktinformation",
      type: "PDF, ca. 850 KB",
      url: "/erstinformation.pdf"
    },
    {
      title: "Versicherungsbedingungen (AVB)",
      type: "PDF, ca. 1,2 MB",
      url: "/erstinformation.pdf"
    }
  ];

  const heroFeatures = [
    { icon: Phone, title: "24/7 Krisenhotline", subtitle: "Sofort erreichbar" },
    { icon: CheckCircle2, title: "Risk Academy", subtitle: "Kostenlos inkludiert" },
    { icon: Shield, title: "Bis 10 Mio. €", subtitle: "Deckungssumme" }
  ];

  return (
    <BaseTariffPage
      companyName="Hiscox"
      companyColor={HISCOX_COLOR}
      productRating={{ value: 4.9, count: 476 }}
      priceRange="65-235"
      pageTitle="Hiscox Cyberversicherung für Ärzte & Praxen 2025 | Marktführer seit 2011"
      metaDescription="Hiscox Cyberversicherung mit 24/7 Krisenhotline, kostenlosem Cyber-Training & außergewöhnlichen Assistance-Leistungen. Seit 2011 Deutschlands erster Cyber-Versicherer. Jetzt beraten lassen!"
      metaKeywords="Hiscox Cyberversicherung, Cyber-Versicherung Ärzte, Hiscox CyberClear, Arztpraxis Cyberschutz, Hiscox Risk Academy, 24/7 Krisenhotline"
      canonicalUrl="https://praxis-versichert.de/tarifwissen/hiscox"
      heroLabel="Deutschlands erster Cyber-Versicherer seit 2011"
      heroTitle="Hiscox Cyberversicherung für medizinische Praxen"
      heroDescription="Umfassender Cyber-Schutz mit 24/7-Krisenhotline, kostenloser Risk Academy und außergewöhnlichen Assistance-Leistungen. Seit über 10 Jahren Marktführer mit ausgezeichnetem Schadenservice (4,7 von 5 Sternen)."
      heroFeatures={heroFeatures}
      alertTitle="Warum Hiscox?"
      alertDescription="Als Pionier der Cyber-Versicherung in Deutschland seit 2011 bietet Hiscox nicht nur umfassenden Schutz, sondern auch einzigartige Assistance-Leistungen: 24/7-Krisenhotline, kostenlose Cyber-Trainings für Ihr Team und eine der schnellsten Schadensregulierungen am Markt. Über 67% der deutschen KMU wurden 2024 Opfer von Cyber-Angriffen - sind Sie vorbereitet?"
      coverageAreas={coverageAreas}
      targetGroups={targetGroups}
      insuranceConditions={insuranceConditions}
      faqs={faqs}
      documents={documents}
      caseStudies={caseStudies}
    />
  );
};

export default HiscoxTarif;
